import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Activity, Server, Database, AlertTriangle, CheckCircle2, ShieldAlert, Zap,
  Gauge, Workflow, Search, Sparkles, SendHorizonal, RefreshCw, ArrowLeft,
  Clock, Users, TriangleAlert, PlugZap, ListChecks, LineChart, Rocket, UserSearch,
  Bell, Download, Settings, GitCommitHorizontal, BarChart3,
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL;

const TONE = {
  operational: { dot: '#10b981', text: 'text-emerald-600', bg: 'bg-emerald-50', ring: 'ring-emerald-200', label: 'Operational' },
  warning: { dot: '#f59e0b', text: 'text-amber-600', bg: 'bg-amber-50', ring: 'ring-amber-200', label: 'Warning' },
  degraded: { dot: '#f97316', text: 'text-orange-600', bg: 'bg-orange-50', ring: 'ring-orange-200', label: 'Degraded' },
  critical: { dot: '#ef4444', text: 'text-red-600', bg: 'bg-red-50', ring: 'ring-red-200', label: 'Critical' },
  unmonitored: { dot: '#94a3b8', text: 'text-slate-400', bg: 'bg-slate-50', ring: 'ring-slate-200', label: 'Not Configured' },
  unknown: { dot: '#94a3b8', text: 'text-slate-400', bg: 'bg-slate-50', ring: 'ring-slate-200', label: 'Unknown' },
};
const tone = (s) => TONE[s] || TONE.unknown;

const Dot = ({ status, pulse }) => (
  <span className="relative inline-flex h-2.5 w-2.5">
    {pulse && status === 'operational' && (
      <span className="absolute inline-flex h-full w-full rounded-full opacity-60 animate-ping" style={{ background: tone(status).dot }} />
    )}
    <span className="relative inline-flex rounded-full h-2.5 w-2.5" style={{ background: tone(status).dot }} />
  </span>
);

const relTime = (iso) => {
  if (!iso) return '—';
  try { const d = (Date.now() - new Date(iso).getTime()) / 1000;
    if (d < 60) return `${Math.max(0, Math.floor(d))}s ago`;
    if (d < 3600) return `${Math.floor(d / 60)}m ago`;
    if (d < 86400) return `${Math.floor(d / 3600)}h ago`;
    return `${Math.floor(d / 86400)}d ago`;
  } catch { return iso; }
};
const clock = (iso) => { try { return new Date(iso).toLocaleTimeString('en-AU', { hour: '2-digit', minute: '2-digit', second: '2-digit' }); } catch { return '—'; } };

const TABS = [
  { key: 'overview', label: 'Overview', icon: Gauge },
  { key: 'alerts', label: 'Alerts', icon: Bell },
  { key: 'issues', label: 'Issues', icon: TriangleAlert },
  { key: 'activity', label: 'Live Activity', icon: Activity },
  { key: 'services', label: 'Services', icon: Server },
  { key: 'integrations', label: 'Integrations', icon: PlugZap },
  { key: 'automations', label: 'Automations', icon: Workflow },
  { key: 'performance', label: 'Performance', icon: LineChart },
  { key: 'journey', label: 'User Diagnostics', icon: UserSearch },
  { key: 'deployments', label: 'Deployments', icon: Rocket },
  { key: 'logs', label: 'Logs', icon: ListChecks },
];

export default function SystemDiagnostics() {
  const { token, user } = useAuth();
  const navigate = useNavigate();
  const auth = useMemo(() => ({ headers: { Authorization: `Bearer ${token}` } }), [token]);
  const [tab, setTab] = useState('overview');
  const [overview, setOverview] = useState(null);
  const [lastSync, setLastSync] = useState(null);
  const [alertCount, setAlertCount] = useState(0);
  const isAdmin = user && (user.is_master || user.role === 'admin');

  const load = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/api/diagnostics/overview`, auth);
      setOverview(data);
      setLastSync(new Date());
    } catch (e) { /* silent — badge shows last good state */ }
    try {
      const { data } = await axios.get(`${API}/api/diagnostics/alerts`, auth);
      setAlertCount(data.unacknowledged || 0);
    } catch (e) { /* ignore */ }
  }, [auth]);

  useEffect(() => {
    if (!isAdmin) return;
    load();
    const id = setInterval(() => { if (!document.hidden) load(); }, 15000);
    return () => clearInterval(id);
  }, [load, isAdmin]);

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0B1120] flex items-center justify-center p-6 sysdiag" data-testid="diagnostics-forbidden">
        <div className="text-center max-w-sm">
          <ShieldAlert className="w-10 h-10 text-slate-500 mx-auto mb-4" />
          <h1 className="text-xl font-bold text-white">Restricted</h1>
          <p className="text-slate-400 mt-2 text-sm">System Diagnostics is available to administrators only.</p>
          <button onClick={() => navigate('/')} className="mt-5 inline-flex items-center gap-2 text-sm font-semibold text-white bg-slate-800 hover:bg-slate-700 px-4 py-2 rounded-lg" data-testid="diagnostics-back-home">
            <ArrowLeft className="w-4 h-4" /> Back to Home
          </button>
        </div>
      </div>
    );
  }

  const ov = overview?.overall || { label: '—', tone: 'unknown' };
  const t = tone(ov.tone);

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100 font-sf sysdiag" data-testid="system-diagnostics-page">
      {/* Top chrome */}
      <header className="sticky top-0 z-30 bg-[#0B1120]/90 backdrop-blur border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-4 sm:px-6 py-3 flex items-center gap-3">
          <button onClick={() => navigate('/')} className="p-2 -ml-2 rounded-lg hover:bg-white/5 text-slate-400" data-testid="diagnostics-home-btn"><ArrowLeft className="w-5 h-5" /></button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-sky-500 to-indigo-600 grid place-items-center shadow-lg shadow-indigo-900/40"><Activity className="w-4 h-4 text-white" /></div>
            <div>
              <h1 className="text-[15px] font-bold tracking-tight leading-none">System Diagnostics</h1>
              <p className="text-[11px] text-slate-400 leading-none mt-1">DerwentAI health control room</p>
            </div>
          </div>
          <div className="ml-auto flex items-center gap-3">
            <button onClick={() => setTab('alerts')} className="relative p-2 rounded-lg hover:bg-white/5 text-slate-300" title="Alerts" data-testid="diagnostics-alert-bell">
              <Bell className="w-4.5 h-4.5 w-5 h-5" />
              {alertCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 min-w-[16px] h-4 px-1 rounded-full bg-red-500 text-white text-[10px] font-bold grid place-items-center" data-testid="alert-bell-count">{alertCount}</span>
              )}
            </button>
            <div className={`hidden sm:flex items-center gap-2 px-3 py-1.5 rounded-full ring-1 ${t.ring} ${t.bg}`} data-testid="diagnostics-overall-pill">
              <Dot status={ov.tone} pulse />
              <span className={`text-[12px] font-bold ${t.text}`}>{ov.label}</span>
            </div>
            <button onClick={load} className="p-2 rounded-lg hover:bg-white/5 text-slate-400" title="Refresh now" data-testid="diagnostics-refresh">
              <RefreshCw className="w-4 h-4" />
            </button>
          </div>
        </div>
        {/* tabs */}
        <div className="max-w-[1400px] mx-auto px-2 sm:px-5 overflow-x-auto scrollbar-none">
          <div className="flex items-center gap-1 pb-2">
            {TABS.map((tb) => {
              const Icon = tb.icon; const active = tab === tb.key;
              return (
                <button key={tb.key} onClick={() => setTab(tb.key)} data-testid={`diagnostics-tab-${tb.key}`}
                  className={`flex items-center gap-1.5 whitespace-nowrap px-3 py-1.5 rounded-lg text-[12.5px] font-semibold transition-colors ${active ? 'bg-white/10 text-white' : 'text-slate-400 hover:text-slate-200 hover:bg-white/5'}`}>
                  <Icon className="w-3.5 h-3.5" /> {tb.label}
                </button>
              );
            })}
          </div>
        </div>
      </header>

      <main className="max-w-[1400px] mx-auto px-4 sm:px-6 py-6">
        <AnimatePresence mode="wait">
          <motion.div key={tab} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0 }} transition={{ duration: 0.18 }}>
            {tab === 'overview' && <OverviewTab overview={overview} lastSync={lastSync} auth={auth} />}
            {tab === 'alerts' && <AlertsTab auth={auth} onChange={load} />}
            {tab === 'issues' && <IssuesTab auth={auth} />}
            {tab === 'activity' && <ActivityTab auth={auth} />}
            {tab === 'services' && <ServicesTab auth={auth} />}
            {tab === 'integrations' && <IntegrationsTab auth={auth} />}
            {tab === 'automations' && <AutomationsTab auth={auth} />}
            {tab === 'performance' && <PerformanceTab auth={auth} />}
            {tab === 'journey' && <JourneyTab auth={auth} />}
            {tab === 'deployments' && <DeploymentsTab auth={auth} />}
            {tab === 'logs' && <LogsTab auth={auth} token={token} />}
          </motion.div>
        </AnimatePresence>
      </main>
    </div>
  );
}

// ───────────────────────── shared UI ─────────────────────────
const Card = ({ children, className = '', ...rest }) => (
  <div className={`rounded-2xl bg-white/[0.04] ring-1 ring-white/10 ${className}`} {...rest}>{children}</div>
);
const SectionTitle = ({ icon: Icon, children, right }) => (
  <div className="flex items-center gap-2 mb-3">
    {Icon && <Icon className="w-4 h-4 text-slate-400" />}
    <h2 className="text-sm font-bold text-slate-200">{children}</h2>
    {right && <div className="ml-auto">{right}</div>}
  </div>
);
const Loading = () => <div className="py-16 text-center text-slate-500 text-sm" data-testid="diagnostics-loading">Loading live data…</div>;

const useFetch = (path, auth, deps = []) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const reload = useCallback(async () => {
    try { const r = await axios.get(`${API}/api/diagnostics/${path}`, auth); setData(r.data); }
    catch { setData(null); } finally { setLoading(false); }
  }, [path, auth]); // eslint-disable-line
  useEffect(() => { let on = true; setLoading(true);
    (async () => { try { const r = await axios.get(`${API}/api/diagnostics/${path}`, auth); if (on) setData(r.data); } catch { if (on) setData(null); } finally { if (on) setLoading(false); } })();
    const id = setInterval(() => { if (!document.hidden) reload(); }, 15000);
    return () => { on = false; clearInterval(id); };
  }, deps); // eslint-disable-line
  return { data, loading, reload };
};

// ───────────────────────── Overview ─────────────────────────
const Stat = ({ label, value, sub, icon: Icon, accent }) => (
  <Card className="p-4" data-testid={`stat-${label.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
    <div className="flex items-center gap-2 text-slate-400 text-[11px] font-semibold uppercase tracking-wide">
      {Icon && <Icon className="w-3.5 h-3.5" />}{label}
    </div>
    <div className={`mt-1.5 text-2xl font-extrabold tracking-tight ${accent || 'text-white'}`}>{value}</div>
    {sub && <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>}
  </Card>
);

function OverviewTab({ overview, lastSync, auth }) {
  const [summary, setSummary] = useState(null);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [q, setQ] = useState('');
  const [ask, setAsk] = useState([]);
  const [asking, setAsking] = useState(false);
  const chatRef = useRef(null);

  useEffect(() => {
    let on = true;
    (async () => { try { const r = await axios.get(`${API}/api/diagnostics/summary`, auth); if (on) setSummary(r.data.summary); } catch { if (on) setSummary(null); } finally { if (on) setSummaryLoading(false); } })();
    return () => { on = false; };
  }, [auth]);

  const submitAsk = async (e) => {
    e?.preventDefault();
    const question = q.trim(); if (!question || asking) return;
    setAsk((a) => [...a, { role: 'user', text: question }]); setQ(''); setAsking(true);
    try {
      const r = await axios.post(`${API}/api/diagnostics/ask`, { question }, auth);
      setAsk((a) => [...a, { role: 'ai', text: r.data.answer }]);
    } catch { setAsk((a) => [...a, { role: 'ai', text: 'The assistant hit an error. Please try again.' }]); }
    finally { setAsking(false); setTimeout(() => chatRef.current?.scrollTo(0, 1e9), 50); }
  };

  if (!overview) return <Loading />;
  const t = tone(overview.overall.tone);
  return (
    <div className="space-y-5">
      {/* Big status banner */}
      <Card className={`p-5 sm:p-6 ring-2 ${t.ring}`} data-testid="overview-status-banner">
        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex items-center gap-3">
            <div className="relative grid place-items-center w-14 h-14 rounded-2xl" style={{ background: `${t.dot}1a` }}>
              <span className="absolute inline-flex h-3 w-3 rounded-full animate-ping opacity-60" style={{ background: t.dot }} />
              <span className="relative inline-flex h-3.5 w-3.5 rounded-full" style={{ background: t.dot }} />
            </div>
            <div>
              <p className="text-[11px] uppercase tracking-wider text-slate-400 font-semibold">DerwentAI System Status</p>
              <h2 className={`text-2xl sm:text-3xl font-extrabold tracking-tight ${t.text}`}>{overview.overall.label}</h2>
            </div>
          </div>
          <div className="sm:ml-auto flex items-center gap-5 text-[12px] text-slate-400">
            <div><div className="text-slate-500">Last System Check</div><div className="text-slate-200 font-semibold tabular-nums">{clock(overview.last_check)}</div></div>
            <div><div className="text-slate-500">Next Check</div><div className="text-emerald-400 font-semibold flex items-center gap-1"><Zap className="w-3 h-3" /> Live</div></div>
          </div>
        </div>
      </Card>

      {/* AI summary */}
      <Card className="p-5" data-testid="overview-ai-summary">
        <SectionTitle icon={Sparkles}>DerwentAI Diagnostic Summary</SectionTitle>
        {summaryLoading ? <div className="h-4 w-2/3 bg-white/10 rounded animate-pulse" />
          : <p className="text-[13.5px] leading-relaxed text-slate-300">{summary || 'Summary unavailable right now.'}</p>}
      </Card>

      {/* Stat grid */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <Stat label="Active Users" value={overview.active_users} sub="last 15 min" icon={Users} />
        <Stat label="Errors 24h" value={overview.errors_24h} icon={AlertTriangle} accent={overview.errors_24h > 0 ? 'text-red-400' : 'text-white'} />
        <Stat label="Warnings 24h" value={overview.warnings_24h} icon={TriangleAlert} accent={overview.warnings_24h > 0 ? 'text-amber-400' : 'text-white'} />
        <Stat label="Avg Response" value={`${Math.round(overview.avg_response_ms)}ms`} sub={`${overview.requests_24h} requests`} icon={Gauge} />
        <Stat label="Database" value={overview.database?.query_ms != null ? `${overview.database.query_ms}ms` : '—'} sub={tone(overview.database?.status).label} icon={Database} accent={tone(overview.database?.status).text === 'text-emerald-600' ? 'text-emerald-400' : undefined} />
      </div>

      {/* Ask DerwentAI */}
      <Card className="p-5" data-testid="overview-ask">
        <SectionTitle icon={Search}>Ask DerwentAI</SectionTitle>
        <div ref={chatRef} className="max-h-64 overflow-y-auto space-y-2.5 mb-3 scrollbar-none">
          {ask.length === 0 && (
            <div className="text-[12px] text-slate-500">Try: "Which system has the most errors today?" · "Why did a portal fail?" · "Are document uploads healthy?"</div>
          )}
          {ask.map((m, i) => (
            <div key={i} className={`flex ${m.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] px-3.5 py-2 rounded-2xl text-[13px] leading-relaxed ${m.role === 'user' ? 'bg-indigo-600 text-white' : 'bg-white/[0.06] text-slate-200 ring-1 ring-white/10'}`}>{m.text}</div>
            </div>
          ))}
          {asking && <div className="text-[12px] text-slate-500">Analysing live diagnostics…</div>}
        </div>
        <form onSubmit={submitAsk} className="flex items-center gap-2">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Ask about system health…" data-testid="ask-input"
            className="flex-1 bg-white/[0.06] ring-1 ring-white/10 rounded-xl px-3.5 py-2.5 text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:ring-indigo-400" />
          <button type="submit" disabled={asking || !q.trim()} data-testid="ask-submit" className="p-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white"><SendHorizonal className="w-4 h-4" /></button>
        </form>
      </Card>
    </div>
  );
}

// ───────────────────────── Services ─────────────────────────
function ServicesTab({ auth }) {
  const { data, loading } = useFetch('services', auth, [auth]);
  if (loading && !data) return <Loading />;
  const services = data?.services || [];
  return (
    <div>
      <SectionTitle icon={Server} right={<span className="text-[11px] text-slate-500">{services.length} services · auto-refresh 15s</span>}>Live System Health</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {services.map((s) => {
          const t = tone(s.status);
          return (
            <Card key={s.key} className={`p-4 ring-1 ${t.ring}`} data-testid={`service-card-${s.key}`}>
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-2"><Dot status={s.status} pulse /><span className="text-[13.5px] font-bold text-slate-100">{s.label}</span></div>
                <span className={`text-[10.5px] font-bold uppercase tracking-wide ${t.text}`}>{t.label}</span>
              </div>
              {s.status === 'unmonitored' ? (
                <p className="mt-2 text-[11.5px] text-slate-500">Monitoring Not Configured — no activity observed yet.</p>
              ) : (
                <div className="mt-3 grid grid-cols-3 gap-2 text-center">
                  <div><div className="text-[15px] font-extrabold tabular-nums text-slate-100">{s.response_ms ? `${Math.round(s.response_ms)}ms` : '—'}</div><div className="text-[9.5px] uppercase tracking-wide text-slate-500">Response</div></div>
                  <div><div className={`text-[15px] font-extrabold tabular-nums ${s.errors_today > 0 ? 'text-red-400' : 'text-slate-100'}`}>{s.errors_today}</div><div className="text-[9.5px] uppercase tracking-wide text-slate-500">Errors</div></div>
                  <div><div className="text-[12px] font-semibold text-slate-300">{s.last_success_ago || '—'}</div><div className="text-[9.5px] uppercase tracking-wide text-slate-500">Last OK</div></div>
                </div>
              )}
              {s.last_error && <div className="mt-2 text-[11px] text-red-400/90 truncate">Last error: {s.last_error.status} · {s.last_error.ago}</div>}
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Integrations ─────────────────────────
function IntegrationsTab({ auth }) {
  const { data, loading } = useFetch('integrations', auth, [auth]);
  if (loading && !data) return <Loading />;
  const items = data?.integrations || [];
  return (
    <div>
      <SectionTitle icon={PlugZap}>External Integration Status</SectionTitle>
      <p className="text-[12px] text-slate-500 mb-3 -mt-1">If a third-party provider degrades, the issue may be external rather than a DerwentAI bug.</p>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {items.map((it) => {
          const t = tone(it.status);
          return (
            <Card key={it.name} className={`p-4 ring-1 ${t.ring}`} data-testid={`integration-card-${it.name.toLowerCase().replace(/[^a-z]+/g, '-')}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Dot status={it.status} /><span className="text-[13px] font-bold text-slate-100">{it.name}</span></div>
                <span className={`text-[10.5px] font-bold uppercase tracking-wide ${t.text}`}>{t.label}</span>
              </div>
              {it.note && <p className="mt-2 text-[11.5px] text-slate-400">{it.note}</p>}
              <div className="mt-2 flex items-center gap-3 text-[11px] text-slate-500">
                {it.last_success_ago && <span>Last OK {it.last_success_ago}</span>}
                {it.response_ms != null && <span>{Math.round(it.response_ms)}ms</span>}
                {it.external && <span className="ml-auto text-slate-600">External provider</span>}
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Live Activity ─────────────────────────
const ACT_FILTERS = ['all', 'client_portal', 'esign', 'id_verification', 'sms', 'documents', 'ai', 'crm', 'authentication', 'integrations', 'database', 'automations'];
const LEVEL_DOT = { success: '#10b981', info: '#60a5fa', warning: '#f59e0b', error: '#ef4444' };
function ActivityTab({ auth }) {
  const [filter, setFilter] = useState('all');
  const { data, loading } = useFetch(`activity?filter=${filter}`, auth, [auth, filter]);
  const items = data?.activity || [];
  return (
    <div>
      <SectionTitle icon={Activity}>Live System Activity</SectionTitle>
      <div className="flex flex-wrap gap-1.5 mb-4">
        {ACT_FILTERS.map((f) => (
          <button key={f} onClick={() => setFilter(f)} data-testid={`activity-filter-${f}`}
            className={`px-2.5 py-1 rounded-full text-[11px] font-semibold capitalize transition-colors ${filter === f ? 'bg-indigo-600 text-white' : 'bg-white/[0.06] text-slate-400 hover:text-slate-200'}`}>
            {f.replace(/_/g, ' ')}
          </button>
        ))}
      </div>
      {loading && !data ? <Loading /> : (
        <Card className="divide-y divide-white/5" data-testid="activity-feed">
          {items.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No activity in this view yet.</div>}
          {items.map((a, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5" data-testid={`activity-row-${i}`}>
              <span className="w-2 h-2 rounded-full shrink-0" style={{ background: LEVEL_DOT[a.level] || '#94a3b8' }} />
              <span className="text-[11px] tabular-nums text-slate-500 w-20 shrink-0">{clock(a.time)}</span>
              <span className={`text-[13px] ${a.level === 'error' ? 'text-red-400' : a.level === 'warning' ? 'text-amber-400' : 'text-slate-200'}`}>{a.message}</span>
              <span className="ml-auto text-[10px] uppercase tracking-wide text-slate-600 capitalize">{a.category?.replace(/_/g, ' ')}</span>
            </div>
          ))}
        </Card>
      )}
    </div>
  );
}

// ───────────────────────── Issues ─────────────────────────
const SEV = { high: { text: 'text-red-400', bg: 'bg-red-500/10', ring: 'ring-red-500/30', dot: 'critical' }, medium: { text: 'text-orange-400', bg: 'bg-orange-500/10', ring: 'ring-orange-500/30', dot: 'degraded' }, low: { text: 'text-amber-400', bg: 'bg-amber-500/10', ring: 'ring-amber-500/30', dot: 'warning' } };
function IssuesTab({ auth }) {
  const { data, loading, reload } = useFetch('issues', auth, [auth]);
  const issues = data?.issues || [];
  const anomalies = data?.anomalies || [];
  const resolve = async (key) => { try { await axios.post(`${API}/api/diagnostics/issues/${encodeURIComponent(key)}/resolve`, {}, auth); reload(); } catch {} };
  if (loading && !data) return <Loading />;
  return (
    <div className="space-y-5">
      {anomalies.length > 0 && (
        <div>
          <SectionTitle icon={Sparkles}>Smart Issue Detection</SectionTitle>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {anomalies.map((a, i) => (
              <Card key={i} className="p-4 ring-1 ring-amber-500/30 bg-amber-500/[0.04]" data-testid={`anomaly-card-${i}`}>
                <div className="flex items-center gap-2"><Sparkles className="w-4 h-4 text-amber-400" /><span className="text-[13px] font-bold text-amber-300">{a.title}</span></div>
                <p className="text-[12px] text-slate-300 mt-1.5">{a.detail}</p>
              </Card>
            ))}
          </div>
        </div>
      )}
      <div>
        <SectionTitle icon={TriangleAlert} right={<span className="text-[11px] text-slate-500">{issues.length} active · grouped</span>}>Issues Detected</SectionTitle>
        {issues.length === 0 ? (
          <Card className="p-10 text-center" data-testid="issues-empty">
            <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
            <p className="text-slate-200 font-semibold">No active issues detected</p>
            <p className="text-slate-500 text-[12px] mt-1">Errors are grouped automatically. You'll see clusters here the moment they appear.</p>
          </Card>
        ) : (
          <div className="space-y-3">
            {issues.map((iss) => {
              const sev = SEV[iss.severity] || SEV.low;
              return (
                <Card key={iss.key} className={`p-4 ring-1 ${sev.ring}`} data-testid={`issue-card-${iss.key}`}>
                  <div className="flex items-start gap-3">
                    <Dot status={sev.dot} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <span className="text-[13.5px] font-bold text-slate-100">{iss.title}</span>
                        <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${sev.bg} ${sev.text}`}>{iss.severity}</span>
                      </div>
                      <p className="text-[12px] text-slate-400 mt-1">{iss.error}</p>
                      <div className="flex flex-wrap gap-x-5 gap-y-1 mt-2.5 text-[11.5px] text-slate-400">
                        <span><span className="text-slate-500">Occurrences</span> <b className="text-slate-200">{iss.occurrences}</b></span>
                        <span><span className="text-slate-500">Affected users</span> <b className="text-slate-200">{iss.affected_users}</b></span>
                        <span><span className="text-slate-500">First</span> <b className="text-slate-200">{iss.first_ago}</b></span>
                        <span><span className="text-slate-500">Last</span> <b className="text-slate-200">{iss.last_ago}</b></span>
                      </div>
                    </div>
                    <button onClick={() => resolve(iss.key)} data-testid={`issue-resolve-${iss.key}`} className="shrink-0 text-[11px] font-semibold text-slate-300 bg-white/[0.06] hover:bg-white/10 px-3 py-1.5 rounded-lg">Mark Resolved</button>
                  </div>
                </Card>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}

// ───────────────────────── Automations ─────────────────────────
function AutomationsTab({ auth }) {
  const { data, loading } = useFetch('automations', auth, [auth]);
  if (loading && !data) return <Loading />;
  const autos = data?.automations || [];
  return (
    <div>
      <SectionTitle icon={Workflow}>Automation Health</SectionTitle>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
        {autos.map((a) => {
          const t = tone(a.status);
          return (
            <Card key={a.name} className={`p-4 ring-1 ${t.ring}`} data-testid={`automation-card-${a.name}`}>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2"><Dot status={a.status} /><span className="text-[13px] font-bold text-slate-100">{a.label}</span></div>
                <span className={`text-[10.5px] font-bold uppercase ${t.text}`}>{t.label}</span>
              </div>
              <div className="mt-2 flex items-center justify-between text-[11.5px] text-slate-400">
                <span><Clock className="w-3 h-3 inline mr-1" />{a.cadence}</span>
                <span>{a.last_run ? `Last run ${a.last_run_ago}` : (a.note || 'Awaiting first run')}</span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
}

// ───────────────────────── Performance ─────────────────────────
function PerformanceTab({ auth }) {
  const { data, loading } = useFetch('performance', auth, [auth]);
  if (loading && !data) return <Loading />;
  const p = data || {};
  const worse = p.current_p95_ms && p.p95_7d_ms && p.current_p95_ms > p.p95_7d_ms * 2.5;
  return (
    <div className="space-y-5">
      <SectionTitle icon={LineChart}>Performance Monitoring</SectionTitle>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat label="Current p95" value={`${Math.round(p.current_p95_ms || 0)}ms`} sub="last hour" icon={Gauge} accent={worse ? 'text-amber-400' : undefined} />
        <Stat label="24h p95" value={`${Math.round(p.p95_24h_ms || 0)}ms`} />
        <Stat label="7d p95" value={`${Math.round(p.p95_7d_ms || 0)}ms`} />
        <Stat label="Success Rate" value={`${p.success_rate ?? 100}%`} sub={`${p.error_rate ?? 0}% errors`} icon={CheckCircle2} accent="text-emerald-400" />
      </div>
      {worse && (
        <Card className="p-4 ring-1 ring-amber-500/30 bg-amber-500/[0.04]"><div className="flex items-center gap-2 text-amber-300 text-[13px] font-semibold"><AlertTriangle className="w-4 h-4" /> Platform Performance Degradation — current response time is well above the 7-day norm.</div></Card>
      )}
      <div>
        <SectionTitle icon={ListChecks}>Slowest Endpoints (24h)</SectionTitle>
        <Card className="divide-y divide-white/5" data-testid="slowest-endpoints">
          {(p.slowest_endpoints || []).length === 0 && <div className="p-6 text-center text-slate-500 text-sm">Not enough traffic yet to rank endpoints.</div>}
          {(p.slowest_endpoints || []).map((e, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <span className="text-[11px] tabular-nums text-slate-600 w-5">{i + 1}</span>
              <span className="text-[12.5px] text-slate-300 font-mono truncate">{e.path}</span>
              <span className="ml-auto text-[11px] text-slate-500">{e.calls} calls</span>
              <span className={`text-[12.5px] font-bold tabular-nums w-16 text-right ${e.p95_ms > 1500 ? 'text-amber-400' : 'text-slate-200'}`}>{Math.round(e.p95_ms)}ms</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ───────────────────────── User Journey ─────────────────────────
function JourneyTab({ auth }) {
  const [q, setQ] = useState('');
  const [res, setRes] = useState(null);
  const [loading, setLoading] = useState(false);
  const [searched, setSearched] = useState(false);
  const search = async (e) => {
    e?.preventDefault(); if (q.trim().length < 2) return;
    setLoading(true); setSearched(true);
    try { const r = await axios.get(`${API}/api/diagnostics/user-journey?q=${encodeURIComponent(q.trim())}`, auth); setRes(r.data.matches || []); }
    catch { setRes([]); } finally { setLoading(false); }
  };
  return (
    <div>
      <SectionTitle icon={UserSearch}>User Journey Diagnostics</SectionTitle>
      <form onSubmit={search} className="flex items-center gap-2 mb-5 max-w-lg">
        <div className="relative flex-1">
          <Search className="w-4 h-4 text-slate-500 absolute left-3 top-1/2 -translate-y-1/2" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Client name, email, mobile or portal reference…" data-testid="journey-search-input"
            className="w-full bg-white/[0.06] ring-1 ring-white/10 rounded-xl pl-9 pr-3 py-2.5 text-[13px] text-white placeholder:text-slate-500 focus:outline-none focus:ring-indigo-400" />
        </div>
        <button type="submit" data-testid="journey-search-btn" className="px-4 py-2.5 rounded-xl bg-indigo-600 hover:bg-indigo-500 text-white text-[13px] font-semibold">Search</button>
      </form>
      {loading && <Loading />}
      {!loading && searched && (res?.length === 0) && <Card className="p-8 text-center text-slate-500 text-sm" data-testid="journey-empty">No matching client or portal found.</Card>}
      <div className="space-y-4">
        {(res || []).map((m) => (
          <Card key={m.application_id} className="p-4" data-testid={`journey-result-${m.application_id}`}>
            <div className="flex items-center gap-2 flex-wrap mb-3">
              <span className="text-[14px] font-bold text-slate-100">{m.name || 'Client'}</span>
              {m.reference && <span className="text-[11px] font-mono text-slate-400 bg-white/5 px-2 py-0.5 rounded">{m.reference}</span>}
              {m.status && <span className="text-[11px] text-slate-400 capitalize">{m.status.replace(/_/g, ' ')}</span>}
              {m.email && <span className="text-[11px] text-slate-500">{m.email}</span>}
              {m.mobile && <span className="text-[11px] text-slate-500">··{m.mobile}</span>}
            </div>
            {m.first_failure && (
              <div className="mb-3 flex items-center gap-2 text-[12.5px] text-red-400 bg-red-500/10 ring-1 ring-red-500/20 rounded-lg px-3 py-2" data-testid="journey-failure-flag">
                <AlertTriangle className="w-4 h-4" /> Potential failure detected at: <b>{m.first_failure}</b>
              </div>
            )}
            <div className="relative pl-4">
              <div className="absolute left-[6px] top-1 bottom-1 w-px bg-white/10" />
              {m.timeline.map((ev, i) => (
                <div key={i} className="relative flex items-center gap-3 py-1.5">
                  <span className={`absolute -left-[11px] w-2.5 h-2.5 rounded-full ring-2 ring-[#0B1120] ${ev.ok ? 'bg-emerald-500' : 'bg-red-500'}`} />
                  <span className="text-[11px] tabular-nums text-slate-500 w-16">{clock(ev.time)}</span>
                  <span className={`text-[12.5px] ${ev.ok ? 'text-slate-200' : 'text-red-400'}`}>{ev.label}</span>
                  {ev.ok ? <CheckCircle2 className="w-3.5 h-3.5 text-emerald-500 ml-1" /> : <AlertTriangle className="w-3.5 h-3.5 text-red-500 ml-1" />}
                </div>
              ))}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
}

// ───────────────────────── Deployments (real) ─────────────────────────
function DeploymentsTab({ auth }) {
  const { data, loading } = useFetch('deployments', auth, [auth]);
  if (loading && !data) return <Loading />;
  const latest = data?.latest;
  const recent = data?.recent || [];
  if (!latest) {
    return (
      <div>
        <SectionTitle icon={Rocket}>Recent Deployment Monitoring</SectionTitle>
        <Card className="p-8 text-center" data-testid="deployments-empty">
          <Rocket className="w-9 h-9 text-slate-600 mx-auto mb-3" />
          <p className="text-slate-200 font-semibold">No deployments recorded yet</p>
          <p className="text-slate-500 text-[12.5px] mt-1.5">A deployment is marked automatically each time the backend boots with a new version.</p>
        </Card>
      </div>
    );
  }
  const regressed = latest.status === 'regression';
  const stTone = regressed ? 'critical' : latest.status === 'monitoring' ? 'unknown' : 'operational';
  const st = tone(stTone);
  return (
    <div className="space-y-5">
      <SectionTitle icon={Rocket}>Recent Deployment Monitoring</SectionTitle>
      <Card className={`p-5 ring-1 ${st.ring}`} data-testid="deployment-latest">
        <div className="flex flex-wrap items-center gap-3">
          <GitCommitHorizontal className="w-5 h-5 text-slate-400" />
          <div>
            <div className="text-[11px] uppercase tracking-wide text-slate-500">Latest Deployment</div>
            <div className="text-[15px] font-bold text-slate-100 font-mono">{latest.version}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-[11px] text-slate-500">Deployed</div>
            <div className="text-[13px] text-slate-200">{new Date(latest.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</div>
          </div>
        </div>
        <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div><div className="text-[11px] text-slate-500">Pre-deploy errors</div><div className="text-xl font-extrabold text-slate-100">{latest.pre_error_rate}%</div><div className="text-[10px] text-slate-600">{latest.pre_samples} reqs</div></div>
          <div><div className="text-[11px] text-slate-500">Post-deploy errors</div><div className={`text-xl font-extrabold ${regressed ? 'text-red-400' : 'text-slate-100'}`}>{latest.post_error_rate}%</div><div className="text-[10px] text-slate-600">{latest.post_samples} reqs</div></div>
          <div className="sm:col-span-2 flex items-center">
            <span className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full text-[12px] font-bold ${st.bg} ${st.text}`} data-testid="deployment-status">
              <Dot status={stTone} />
              {regressed ? 'Possible regression detected' : latest.status === 'monitoring' ? 'Monitoring — awaiting traffic' : 'Healthy'}
            </span>
          </div>
        </div>
        {latest.flag && <p className="mt-3 text-[12.5px] text-red-400 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> {latest.flag}</p>}
      </Card>
      <div>
        <SectionTitle icon={Clock}>History</SectionTitle>
        <Card className="divide-y divide-white/5" data-testid="deployment-history">
          {recent.map((d, i) => (
            <div key={i} className="flex items-center gap-3 px-4 py-2.5">
              <span className="font-mono text-[12.5px] text-slate-200">{d.version}</span>
              <span className="text-[11px] text-slate-500">{d.note}</span>
              <span className="ml-auto text-[11px] text-slate-500">{new Date(d.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}</span>
            </div>
          ))}
        </Card>
      </div>
    </div>
  );
}

// ───────────────────────── Alerts (smart alerting) ─────────────────────────
const SEVS = ['critical', 'high', 'medium', 'low'];
const SEV_TONE = { critical: 'critical', high: 'degraded', medium: 'warning', low: 'unknown' };
function AlertsTab({ auth, onChange }) {
  const { data, loading, reload } = useFetch('alerts', auth, [auth]);
  const [showSettings, setShowSettings] = useState(false);
  const active = data?.active || [];
  const resolved = data?.recent_resolved || [];
  const act = async (key, verb) => {
    try { await axios.post(`${API}/api/diagnostics/alerts/${encodeURIComponent(key)}/${verb}`, {}, auth); reload(); onChange?.(); } catch {}
  };
  return (
    <div className="space-y-5">
      <SectionTitle icon={Bell} right={
        <button onClick={() => setShowSettings((s) => !s)} data-testid="alert-settings-toggle" className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-300 bg-white/[0.06] hover:bg-white/10 px-3 py-1.5 rounded-lg">
          <Settings className="w-3.5 h-3.5" /> Alert Settings
        </button>
      }>Alerts</SectionTitle>

      {showSettings && <AlertSettings auth={auth} onSaved={() => { setShowSettings(false); reload(); }} />}

      {loading && !data ? <Loading /> : active.length === 0 ? (
        <Card className="p-10 text-center" data-testid="alerts-empty">
          <CheckCircle2 className="w-10 h-10 text-emerald-500 mx-auto mb-3" />
          <p className="text-slate-200 font-semibold">No active alerts</p>
          <p className="text-slate-500 text-[12px] mt-1">Critical issues, outages and regressions will raise an alert here (and notify your chosen channels).</p>
        </Card>
      ) : (
        <div className="space-y-3">
          {active.map((a) => {
            const st = tone(SEV_TONE[a.severity]);
            return (
              <Card key={a.key} className={`p-4 ring-1 ${st.ring} ${a.acknowledged ? 'opacity-60' : ''}`} data-testid={`alert-card-${a.key}`}>
                <div className="flex items-start gap-3">
                  <Dot status={SEV_TONE[a.severity]} pulse={!a.acknowledged} />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="text-[13.5px] font-bold text-slate-100">{a.title}</span>
                      <span className={`text-[10px] font-bold uppercase px-1.5 py-0.5 rounded ${st.bg} ${st.text}`}>{a.severity}</span>
                      {a.acknowledged && <span className="text-[10px] text-slate-500 uppercase">Acknowledged</span>}
                    </div>
                    <p className="text-[12.5px] text-slate-300 mt-1">{a.detail}</p>
                    <div className="flex gap-4 mt-2 text-[11px] text-slate-500">
                      <span>First {relTime(a.first_seen)}</span>
                      <span>Last {relTime(a.last_seen)}</span>
                      <span>×{a.occurrences}</span>
                    </div>
                  </div>
                  <div className="flex flex-col gap-1.5 shrink-0">
                    {!a.acknowledged && <button onClick={() => act(a.key, 'ack')} data-testid={`alert-ack-${a.key}`} className="text-[11px] font-semibold text-slate-300 bg-white/[0.06] hover:bg-white/10 px-3 py-1.5 rounded-lg">Mute</button>}
                    <button onClick={() => act(a.key, 'resolve')} data-testid={`alert-resolve-${a.key}`} className="text-[11px] font-semibold text-slate-300 bg-white/[0.06] hover:bg-white/10 px-3 py-1.5 rounded-lg">Resolve</button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      {resolved.length > 0 && (
        <div>
          <SectionTitle icon={CheckCircle2}>Recently Resolved</SectionTitle>
          <Card className="divide-y divide-white/5">
            {resolved.map((a, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5">
                <span className="w-2 h-2 rounded-full bg-emerald-500" />
                <span className="text-[12.5px] text-slate-300">{a.title}</span>
                <span className="ml-auto text-[11px] text-slate-500">resolved {relTime(a.resolved_at)}</span>
              </div>
            ))}
          </Card>
        </div>
      )}
    </div>
  );
}

function AlertSettings({ auth, onSaved }) {
  const [s, setS] = useState(null);
  const [saving, setSaving] = useState(false);
  useEffect(() => { (async () => { try { const r = await axios.get(`${API}/api/diagnostics/alert-settings`, auth); setS(r.data); } catch {} })(); }, [auth]);
  if (!s) return null;
  const toggle = (ch) => setS({ ...s, channels: { ...s.channels, [ch]: !s.channels[ch] } });
  const save = async () => {
    setSaving(true);
    try { await axios.put(`${API}/api/diagnostics/alert-settings`, { channels: s.channels, min_severity: s.min_severity, multi_client_threshold: Number(s.multi_client_threshold) }, auth); onSaved?.(); } catch {} finally { setSaving(false); }
  };
  const CH = [['slack', 'Slack'], ['in_app', 'In-app'], ['email', 'Email'], ['sms', 'SMS']];
  return (
    <Card className="p-5" data-testid="alert-settings-panel">
      <div className="grid sm:grid-cols-2 gap-5">
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Notification channels</div>
          <div className="flex flex-wrap gap-2">
            {CH.map(([k, lbl]) => (
              <button key={k} onClick={() => toggle(k)} data-testid={`alert-channel-${k}`}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${s.channels[k] ? 'bg-indigo-600 text-white' : 'bg-white/[0.06] text-slate-400'}`}>{lbl}</button>
            ))}
          </div>
          <p className="text-[10.5px] text-slate-600 mt-2">Email/SMS deliver only once a verified provider is configured; Slack & in-app are live.</p>
        </div>
        <div>
          <div className="text-[11px] uppercase tracking-wide text-slate-500 mb-2">Only notify at or above</div>
          <div className="flex gap-2">
            {SEVS.map((sv) => (
              <button key={sv} onClick={() => setS({ ...s, min_severity: sv })} data-testid={`alert-minsev-${sv}`}
                className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold capitalize ${s.min_severity === sv ? 'bg-indigo-600 text-white' : 'bg-white/[0.06] text-slate-400'}`}>{sv}</button>
            ))}
          </div>
          <div className="mt-3 text-[11px] uppercase tracking-wide text-slate-500 mb-1">Multiple-clients threshold</div>
          <input type="number" min={1} value={s.multi_client_threshold} onChange={(e) => setS({ ...s, multi_client_threshold: e.target.value })} data-testid="alert-threshold-input"
            className="w-24 bg-white/[0.06] ring-1 ring-white/10 rounded-lg px-3 py-1.5 text-[13px] text-white" />
        </div>
      </div>
      <div className="mt-4 flex justify-end">
        <button onClick={save} disabled={saving} data-testid="alert-settings-save" className="px-4 py-2 rounded-lg bg-indigo-600 hover:bg-indigo-500 disabled:opacity-40 text-white text-[13px] font-semibold">{saving ? 'Saving…' : 'Save settings'}</button>
      </div>
    </Card>
  );
}

// ───────────────────────── Logs & Trends ─────────────────────────
const LOG_RANGES = [['today', 'Today'], ['yesterday', 'Yesterday'], ['7d', 'Last 7 days'], ['30d', 'Last 30 days']];
const LOG_LEVELS = [['all', 'All'], ['error', 'Errors'], ['warning', 'Warnings']];
function LogsTab({ auth, token }) {
  const [range, setRange] = useState('today');
  const [level, setLevel] = useState('all');
  const { data: logData, loading } = useFetch(`logs?range=${range}&level=${level}`, auth, [auth, range, level]);
  const { data: trendData } = useFetch(`trends?range=${range === 'today' || range === 'yesterday' ? '7d' : range}`, auth, [auth, range]);
  const logs = logData?.logs || [];
  const exportCsv = () => {
    const url = `${API}/api/diagnostics/logs.csv?range=${range}&level=${level}`;
    fetch(url, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.blob()).then((b) => {
        const a = document.createElement('a'); a.href = URL.createObjectURL(b); a.download = `diagnostics-logs-${range}.csv`; a.click();
      }).catch(() => {});
  };
  return (
    <div className="space-y-5">
      <SectionTitle icon={ListChecks} right={
        <button onClick={exportCsv} data-testid="logs-export" className="flex items-center gap-1.5 text-[11.5px] font-semibold text-slate-300 bg-white/[0.06] hover:bg-white/10 px-3 py-1.5 rounded-lg"><Download className="w-3.5 h-3.5" /> Export CSV</button>
      }>Logs &amp; Trends</SectionTitle>

      <div className="flex flex-wrap gap-2">
        {LOG_RANGES.map(([k, lbl]) => (
          <button key={k} onClick={() => setRange(k)} data-testid={`log-range-${k}`} className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${range === k ? 'bg-indigo-600 text-white' : 'bg-white/[0.06] text-slate-400'}`}>{lbl}</button>
        ))}
        <div className="w-px bg-white/10 mx-1" />
        {LOG_LEVELS.map(([k, lbl]) => (
          <button key={k} onClick={() => setLevel(k)} data-testid={`log-level-${k}`} className={`px-3 py-1.5 rounded-lg text-[12px] font-semibold ${level === k ? 'bg-slate-700 text-white' : 'bg-white/[0.06] text-slate-400'}`}>{lbl}</button>
        ))}
      </div>

      {/* Trends */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        <Card className="p-4" data-testid="trend-common-errors">
          <div className="flex items-center gap-2 mb-2"><BarChart3 className="w-4 h-4 text-slate-400" /><span className="text-[12px] font-bold text-slate-200">Most common errors</span></div>
          {(trendData?.most_common_errors || []).length === 0 ? <p className="text-[11.5px] text-slate-500">No errors in range 🎉</p> :
            (trendData.most_common_errors).map((e, i) => (
              <div key={i} className="flex items-center gap-2 py-1"><span className="text-[11.5px] text-slate-300 font-mono truncate flex-1">{e.label}</span><span className="text-[12px] font-bold text-red-400">{e.count}</span></div>
            ))}
        </Card>
        <Card className="p-4" data-testid="trend-unstable">
          <div className="flex items-center gap-2 mb-2"><TriangleAlert className="w-4 h-4 text-slate-400" /><span className="text-[12px] font-bold text-slate-200">Most unstable</span></div>
          {(trendData?.most_unstable || []).length === 0 ? <p className="text-[11.5px] text-slate-500">Not enough data.</p> :
            (trendData.most_unstable).map((e, i) => (
              <div key={i} className="flex items-center gap-2 py-1"><span className="text-[11.5px] text-slate-300 flex-1">{e.service}</span><span className={`text-[12px] font-bold ${e.error_rate > 5 ? 'text-amber-400' : 'text-slate-300'}`}>{e.error_rate}%</span></div>
            ))}
        </Card>
        <Card className="p-4" data-testid="trend-slowest">
          <div className="flex items-center gap-2 mb-2"><Gauge className="w-4 h-4 text-slate-400" /><span className="text-[12px] font-bold text-slate-200">Slowest components</span></div>
          {(trendData?.slowest_components || []).length === 0 ? <p className="text-[11.5px] text-slate-500">Not enough data.</p> :
            (trendData.slowest_components).map((e, i) => (
              <div key={i} className="flex items-center gap-2 py-1"><span className="text-[11.5px] text-slate-300 flex-1">{e.service}</span><span className="text-[12px] font-bold text-slate-200">{Math.round(e.p95_ms)}ms</span></div>
            ))}
        </Card>
      </div>

      {/* Log table */}
      {loading && !logData ? <Loading /> : (
        <Card className="overflow-hidden" data-testid="logs-table">
          <div className="max-h-[480px] overflow-y-auto divide-y divide-white/5">
            {logs.length === 0 && <div className="p-8 text-center text-slate-500 text-sm">No log entries in this range.</div>}
            {logs.map((l, i) => (
              <div key={i} className="flex items-center gap-3 px-4 py-2 text-[12px]">
                <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: l.level === 'error' ? '#ef4444' : '#f59e0b' }} />
                <span className="tabular-nums text-slate-500 w-32 shrink-0">{new Date(l.time).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit', second: '2-digit' })}</span>
                <span className={`font-bold w-10 shrink-0 ${l.status >= 500 ? 'text-red-400' : 'text-amber-400'}`}>{l.status}</span>
                <span className="text-slate-300 w-40 shrink-0 truncate">{l.service}</span>
                <span className="text-slate-400 font-mono truncate flex-1">{l.path}</span>
                <span className="text-slate-500 tabular-nums shrink-0">{Math.round(l.ms)}ms</span>
              </div>
            ))}
          </div>
        </Card>
      )}
    </div>
  );
}
